/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * EnrichmentReconcileView — admin-only queue for reviewing vendor
 * enrichment rows the importer couldn't auto-match.
 *
 * Two flavors of pending row:
 *   1. AMBIGUOUS — 2+ voters at the same (last_name, address) bucket
 *      tied on first-name signal. Pick one to apply the enrichment.
 *   2. UNMATCHED — no voter found at the source address at all. Usually
 *      "Ignore"; occasionally "Mark as new voter" if the vendor knows
 *      about someone we should be asking the clerk about.
 *
 * Pattern: plain axios via `src/api/enrichmentReconcile.ts` + useState/
 * useEffect with cancellation flags, mirroring DataChangesView. NOT
 * React Query — keeps this view self-contained and consistent with the
 * other admin views.
 *
 * Access guard: defense-in-depth role check (role !== 'admin' shows a
 * red card). The nav filter already gates the menu item, but a stale
 * token could land a user here via deeplink.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Users, AlertCircle, CheckCircle2 } from 'lucide-react';

import {
  fetchPendingEnrichment,
  fetchPendingEnrichments,
  resolvePendingEnrichment,
} from '@/api/enrichmentReconcile';
import type {
  EnrichmentResolutionAction,
  EnrichmentTierFilter,
  EnrichmentResolvedFilter,
  PendingEnrichmentRead,
  PendingEnrichmentCandidate,
} from '@/types/enrichmentReconcile';
import { Skeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import { ViewHeader } from '@/components/ViewHeader';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatAddress(
  houseNumber: string | null,
  streetName: string | null,
): string {
  const h = (houseNumber || '').trim();
  const s = (streetName || '').trim();
  if (!h && !s) return '—';
  return [h, s].filter(Boolean).join(' ');
}

function formatPhone(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function payloadString(
  payload: Record<string, unknown> | null,
  key: string,
): string | null {
  if (!payload) return null;
  const v = payload[key];
  if (v == null || v === '') return null;
  return String(v);
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export interface EnrichmentReconcileViewProps {
  userRole?: string;
  onBack?: () => void;
}

export function EnrichmentReconcileView({
  userRole,
  onBack: _onBack,
}: EnrichmentReconcileViewProps) {
  // -------------------------------------------------------------------------
  // Access guard — defense-in-depth on top of the nav filter.
  // Backend may emit lowercase, frontend types vary; uppercase both sides.
  // -------------------------------------------------------------------------
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'ADMIN') {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">
            Access Denied
          </h2>
          <p className="text-fg-secondary">
            Enrichment reconciliation is available to administrators only.
            If you believe this is a mistake, contact your tenant admin.
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [items, setItems] = useState<PendingEnrichmentRead[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tierFilter, setTierFilter] = useState<EnrichmentTierFilter>('all');
  const [resolvedFilter, setResolvedFilter] =
    useState<EnrichmentResolvedFilter>('false');
  const [batchFilter, setBatchFilter] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [reloadCounter, setReloadCounter] = useState(0);

  // Modal state for the currently-being-reconciled row.
  const [reconcileTarget, setReconcileTarget] =
    useState<PendingEnrichmentRead | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);

  // Optimistic "resolved this session" counter for the stats strip.
  const [resolvedThisSession, setResolvedThisSession] = useState(0);

  // -------------------------------------------------------------------------
  // Fetch the queue.
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPendingEnrichments({
      tier: tierFilter,
      reconciled: resolvedFilter,
      import_batch_id: batchFilter || undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) {
          setError('Your role does not have access to enrichment reconciliation.');
        } else if (status) {
          setError(`Backend error ${status} loading pending rows.`);
        } else {
          setError('Could not reach the enrichment-reconcile backend.');
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tierFilter, resolvedFilter, batchFilter, offset, reloadCounter]);

  // Reset to page 0 whenever filters change.
  useEffect(() => {
    setOffset(0);
  }, [tierFilter, resolvedFilter, batchFilter]);

  // -------------------------------------------------------------------------
  // Stats strip
  // -------------------------------------------------------------------------
  const stats = useMemo(() => {
    const list = items || [];
    let ambiguous = 0;
    let unmatched = 0;
    for (const r of list) {
      if (r.reconciled_at != null) continue;
      if (r.match_tier === 'ambiguous') ambiguous++;
      else if (r.match_tier === 'unmatched') unmatched++;
    }
    return { ambiguous, unmatched, resolved: resolvedThisSession };
  }, [items, resolvedThisSession]);

  // Available batch ids in the current page, for the batch picker.
  const batchOptions = useMemo<string[]>(() => {
    if (!items) return [];
    const set = new Set<string>();
    for (const r of items) {
      if (r.import_batch_id) set.add(r.import_batch_id);
    }
    return Array.from(set).sort();
  }, [items]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const openReconcile = useCallback(async (row: PendingEnrichmentRead) => {
    setReconcileTarget(row); // optimistic open; modal renders skeleton
    setReconcileLoading(true);
    try {
      const hydrated = await fetchPendingEnrichment(row.id);
      setReconcileTarget(hydrated);
    } catch {
      // Keep the un-hydrated row open; modal handles the empty candidate
      // list gracefully.
    } finally {
      setReconcileLoading(false);
    }
  }, []);

  const closeReconcile = useCallback(() => {
    setReconcileTarget(null);
    setReconcileLoading(false);
  }, []);

  const handleResolved = useCallback(
    (id: number) => {
      // Optimistic update — drop the row from the list immediately.
      setItems((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
      setTotal((t) => Math.max(0, t - 1));
      setResolvedThisSession((n) => n + 1);
      closeReconcile();
    },
    [closeReconcile],
  );

  const handlePrev = () => setOffset((o) => Math.max(0, o - PAGE_SIZE));
  const handleNext = () => setOffset((o) => o + PAGE_SIZE);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Inbox className="w-6 h-6 text-status-info" />
            Enrichment Reconciliation
          </span>
        }
        tagline="Review vendor enrichment rows that couldn't be auto-matched"
      />

      {/* Filter row */}
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
              Tier
            </span>
            <select
              value={tierFilter}
              onChange={(e) =>
                setTierFilter(e.target.value as EnrichmentTierFilter)
              }
              className="bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm min-w-[140px]"
            >
              <option value="all">All tiers</option>
              <option value="ambiguous">Ambiguous</option>
              <option value="unmatched">Unmatched</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
              Resolved
            </span>
            <select
              value={resolvedFilter}
              onChange={(e) =>
                setResolvedFilter(e.target.value as EnrichmentResolvedFilter)
              }
              className="bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm min-w-[140px]"
            >
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
              <option value="all">All</option>
            </select>
          </label>

          {batchOptions.length > 1 && (
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
                Import batch
              </span>
              <select
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                className="bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm min-w-[200px]"
              >
                <option value="">All batches</option>
                {batchOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          label="Ambiguous (this page)"
          value={stats.ambiguous}
          tone="warning"
          Icon={Users}
        />
        <StatCard
          label="Unmatched (this page)"
          value={stats.unmatched}
          tone="danger"
          Icon={AlertCircle}
        />
        <StatCard
          label="Resolved this session"
          value={stats.resolved}
          tone="success"
          Icon={CheckCircle2}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => setReloadCounter((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
        {loading && !items && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        )}

        {!loading && items && items.length === 0 && (
          <EmptyState
            icon={Inbox}
            title="Nothing to reconcile"
            description={
              resolvedFilter === 'false'
                ? "No unresolved pending rows match your filters. Either the queue is drained or the filters are too narrow."
                : "No pending rows match your filters."
            }
            compact
          />
        )}

        {items && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base text-left">
                  <th className="py-2 px-3 text-fg-muted font-semibold">Name</th>
                  <th className="py-2 px-3 text-fg-muted font-semibold">
                    Source address
                  </th>
                  <th className="py-2 px-3 text-fg-muted font-semibold">Tier</th>
                  <th className="py-2 px-3 text-fg-muted font-semibold text-right">
                    Candidates
                  </th>
                  <th className="py-2 px-3 text-fg-muted font-semibold">
                    Vendor signal
                  </th>
                  <th className="py-2 px-3 text-fg-muted font-semibold">
                    Drop
                  </th>
                  <th className="py-2 px-3 text-fg-muted font-semibold text-right">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const partyLabel = payloadString(
                    row.raw_payload,
                    'calculated_party_label',
                  );
                  const phone = payloadString(row.raw_payload, 'primary_phone_raw');
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-border-base hover:bg-bg-base/40"
                    >
                      <td className="py-2 px-3">
                        <div className="text-fg-primary font-medium">
                          {row.source_last_name}, {row.source_first_name}
                        </div>
                        {row.reconciled_at && (
                          <div className="text-[11px] text-fg-muted">
                            resolved {formatDate(row.reconciled_at)} —{' '}
                            {row.reconciliation_action ?? '—'}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-fg-secondary">
                        {formatAddress(
                          row.source_house_number,
                          row.source_street_name,
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <TierBadge tier={row.match_tier} />
                      </td>
                      <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                        {row.candidate_voter_ids.length || '—'}
                      </td>
                      <td className="py-2 px-3 text-fg-secondary">
                        {partyLabel && (
                          <div className="text-xs">{partyLabel}</div>
                        )}
                        {phone && (
                          <div className="text-[11px] text-fg-muted tabular-nums">
                            {formatPhone(phone)}
                          </div>
                        )}
                        {!partyLabel && !phone && (
                          <span className="text-fg-muted">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-fg-muted text-xs tabular-nums">
                        {row.vendor}
                        <br />
                        {row.drop_date}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {row.reconciled_at ? (
                          <span className="text-[11px] text-fg-muted italic">
                            done
                          </span>
                        ) : (
                          <button
                            onClick={() => openReconcile(row)}
                            className="bg-accent hover:bg-accent-hover text-white text-xs font-bold px-3 py-1.5 rounded"
                          >
                            Reconcile…
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between gap-2 pt-3 text-xs text-fg-muted">
              <div>
                Showing {start.toLocaleString()}–{end.toLocaleString()} of{' '}
                {total.toLocaleString()}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  disabled={!hasPrev || loading}
                  className="px-3 py-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary"
                >
                  ← Prev
                </button>
                <button
                  onClick={handleNext}
                  disabled={!hasNext || loading}
                  className="px-3 py-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {reconcileTarget && (
        <ReconcileModal
          row={reconcileTarget}
          loading={reconcileLoading}
          onClose={closeReconcile}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  tone,
  Icon,
}: {
  label: string;
  value: number;
  tone: 'success' | 'danger' | 'warning' | 'info';
  Icon: typeof Users;
}) {
  const palette: Record<string, { bg: string; border: string; text: string }> = {
    success: {
      bg: 'bg-status-success-bg/25',
      border: 'border-status-success-bg/50',
      text: 'text-status-success',
    },
    danger: {
      bg: 'bg-status-danger-bg/25',
      border: 'border-status-danger-bg/50',
      text: 'text-status-danger',
    },
    warning: {
      bg: 'bg-status-warning-bg/25',
      border: 'border-status-warning-bg/50',
      text: 'text-status-warning',
    },
    info: {
      bg: 'bg-status-info-bg/25',
      border: 'border-status-info-bg/50',
      text: 'text-status-info',
    },
  };
  const p = palette[tone];
  return (
    <div className={`rounded-lg border px-3 py-3 ${p.bg} ${p.border}`}>
      <div
        className={`text-[10px] uppercase tracking-wider ${p.text} flex items-center gap-1`}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden="true" />
        {label}
      </div>
      <div className="mt-0.5 text-3xl font-bold text-fg-primary tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    ambiguous: {
      bg: 'bg-status-warning-bg/40 border-status-warning-bg/50',
      text: 'text-status-warning',
      label: 'Ambiguous',
    },
    unmatched: {
      bg: 'bg-status-danger-bg/40 border-status-danger-bg/50',
      text: 'text-status-danger',
      label: 'Unmatched',
    },
  };
  const m = map[tier] ?? {
    bg: 'bg-bg-elevated border-border-base',
    text: 'text-fg-muted',
    label: tier,
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold uppercase tracking-wide ${m.bg} ${m.text}`}
    >
      {m.label}
    </span>
  );
}

/**
 * Reconcile modal — tier-aware. Ambiguous gets a candidate pick-list
 * with a "Confirm match" button; unmatched gets "Ignore" /
 * "Mark as new voter". Both support a free-text notes field.
 */
function ReconcileModal({
  row,
  loading,
  onClose,
  onResolved,
}: {
  row: PendingEnrichmentRead;
  loading: boolean;
  onClose: () => void;
  onResolved: (id: number) => void;
}) {
  const [selectedVoterId, setSelectedVoterId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState<EnrichmentResolutionAction | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = useCallback(
    async (action: EnrichmentResolutionAction) => {
      setSubmitError(null);
      if (action === 'matched' && selectedVoterId == null) {
        setSubmitError('Pick a candidate voter first.');
        return;
      }
      setSubmitting(action);
      try {
        await resolvePendingEnrichment(row.id, {
          action,
          matched_voter_id: action === 'matched' ? selectedVoterId : null,
          notes: notes.trim() || null,
        });
        onResolved(row.id);
      } catch (e: any) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        setSubmitError(
          detail
            ? String(detail)
            : status
              ? `Backend error ${status}.`
              : 'Could not reach the backend.',
        );
        setSubmitting(null);
      }
    },
    [row.id, selectedVoterId, notes, onResolved],
  );

  const isAmbiguous = row.match_tier === 'ambiguous';
  const candidates: PendingEnrichmentCandidate[] = row.candidates || [];
  const phone = payloadString(row.raw_payload, 'primary_phone_raw');
  const partyLabel = payloadString(row.raw_payload, 'calculated_party_label');

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-40"
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Reconcile enrichment row"
        className="fixed inset-y-0 right-0 w-full max-w-lg bg-bg-base border-l border-border-base shadow-2xl z-50 flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border-base bg-bg-raised/60">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-fg-muted font-semibold mb-0.5">
              {isAmbiguous ? 'Ambiguous match' : 'Unmatched row'}
            </div>
            <h2 className="text-xl font-bold text-fg-primary truncate">
              {row.source_last_name}, {row.source_first_name}
            </h2>
            <div className="text-xs text-fg-muted mt-0.5 truncate">
              {formatAddress(row.source_house_number, row.source_street_name)} ·{' '}
              {row.vendor} · {row.drop_date}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Vendor signal */}
          {(phone || partyLabel) && (
            <div className="rounded-lg border border-border-base bg-bg-raised p-3 text-sm space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
                Vendor signal
              </div>
              {partyLabel && (
                <div className="text-fg-secondary">
                  Party: <span className="text-fg-primary">{partyLabel}</span>
                </div>
              )}
              {phone && (
                <div className="text-fg-secondary">
                  Phone:{' '}
                  <span className="text-fg-primary tabular-nums">
                    {formatPhone(phone)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Candidate pick-list (ambiguous only) */}
          {isAmbiguous && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
                Pick the correct voter
              </div>
              {loading && candidates.length === 0 && (
                <div className="space-y-2">
                  <Skeleton className="h-14" />
                  <Skeleton className="h-14" />
                </div>
              )}
              {!loading && candidates.length === 0 && (
                <div className="text-sm text-fg-muted italic">
                  Could not load candidate voters.
                </div>
              )}
              {candidates.map((c) => (
                <label
                  key={c.voter_id}
                  className={`block rounded border p-3 cursor-pointer transition-colors ${
                    selectedVoterId === c.voter_id
                      ? 'border-accent bg-accent/10'
                      : 'border-border-base bg-bg-raised hover:bg-bg-elevated'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="candidate"
                      checked={selectedVoterId === c.voter_id}
                      onChange={() => setSelectedVoterId(c.voter_id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-fg-primary font-medium">
                        {c.first_name} {c.last_name}
                      </div>
                      <div className="text-xs text-fg-muted">
                        {formatAddress(c.house_number, c.street_name)}
                        {c.city ? `, ${c.city}` : ''}
                      </div>
                      <div className="text-[11px] text-fg-muted mt-0.5">
                        {c.party ? `Party ${c.party}` : 'No party'}
                        {c.birth_year ? ` · b. ${c.birth_year}` : ''}
                        {c.precinct_num ? ` · precinct ${c.precinct_num}` : ''}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Unmatched explainer */}
          {!isAmbiguous && (
            <div className="rounded-lg border border-status-warning-bg/40 bg-status-warning-bg/20 p-3 text-sm text-status-warning">
              No voter was found at this source address. Usually the right
              call is to <strong>ignore</strong> — the vendor often knows
              about voters we don't have, or the address format differs.
              Use <strong>Mark as new voter</strong> if you want to flag
              this for the "voters to ask the clerk about" report (no voter
              row is created).
            </div>
          )}

          {/* Notes */}
          <label className="block space-y-1">
            <span className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">
              Notes (optional)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Reason for this resolution, or anything to remember…"
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            />
          </label>

          {submitError && (
            <div className="rounded bg-status-danger-bg/30 border border-status-danger-bg text-status-danger text-sm p-3">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer action buttons */}
        <div className="border-t border-border-base p-4 bg-bg-raised/60 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting != null}
            className="px-3 py-2 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-50 text-fg-primary text-sm"
          >
            Cancel
          </button>
          {isAmbiguous ? (
            <button
              onClick={() => submit('matched')}
              disabled={submitting != null || selectedVoterId == null}
              className="px-3 py-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold"
            >
              {submitting === 'matched' ? 'Saving…' : 'Confirm match'}
            </button>
          ) : (
            <>
              <button
                onClick={() => submit('mark_as_new_voter')}
                disabled={submitting != null}
                className="px-3 py-2 rounded bg-status-warning-bg hover:bg-status-warning-bg/80 disabled:opacity-50 text-white text-sm font-bold"
              >
                {submitting === 'mark_as_new_voter'
                  ? 'Saving…'
                  : 'Mark as new voter'}
              </button>
              <button
                onClick={() => submit('ignored')}
                disabled={submitting != null}
                className="px-3 py-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 text-white text-sm font-bold"
              >
                {submitting === 'ignored' ? 'Saving…' : 'Ignore'}
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export default EnrichmentReconcileView;
